import * as React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useNotification } from "../../context/notification-provider";
import MappingForm, {
  FormFieldValue,
  MappingFormState,
  MappingFormValues,
  validateMapping,
} from "./mapping-form";
import { updateMapping } from "./service";
import { MdaExtractMappingDetail } from "./types";
import useMdaSourceColumns from "./use-mda-source-columns";

const MappingEditDialog = ({
  selectedMapping,
  open,
  onClose,
}: {
  selectedMapping: MdaExtractMappingDetail;
  open: boolean;
  onClose: () => void;
}) => {
  const { showNotification } = useNotification();
  const { data: sourceColumns } = useMdaSourceColumns();
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const [formState, setFormState] = React.useState<MappingFormState>({
    values: { ...selectedMapping },
    errors: {},
  });
  const formValues = formState.values;

  const handleFieldChange = React.useCallback(
    (name: keyof MappingFormValues, value: FormFieldValue) => {
      setFormState((previousState) => ({
        ...previousState,
        values: { ...previousState.values, [name]: value },
      }));
    },
    [],
  );

  const handleSubmit = React.useCallback(async () => {
    const { issues } = validateMapping(formValues);

    if (issues.length > 0) {
      setFormState((previousState) => ({
        ...previousState,
        errors: Object.fromEntries(
          issues.map((issue) => [issue.path[0], issue.message]),
        ),
      }));
      return;
    }

    setFormState((previousState) => ({ ...previousState, errors: {} }));
    setIsSubmitting(true);

    try {
      await updateMapping({
        id: selectedMapping.id,
        sourceColumn: formValues.sourceColumn ?? "",
        destinationColumn: formValues.destinationColumn ?? null,
        columnPosition: selectedMapping.columnPosition,
        enabled: formValues.enabled ?? true,
      });

      showNotification({
        message: "Column mapping updated successfully.",
        severity: "success",
        duration: 3000,
      });

      onClose();
    } catch (updateError) {
      showNotification({
        message: `Failed to update column mapping. Reason: ${(updateError as Error).message}`,
        severity: "error",
        duration: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [formValues, selectedMapping, showNotification, onClose]);

  return (
    <Dialog open={open} onClose={() => onClose()} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ borderBottom: 2, mb: 2, borderColor: "divider" }}>
        Edit Column Mapping
      </DialogTitle>
      <DialogContent sx={{ pb: 0 }}>
        <MappingForm
          formState={formState}
          sourceColumns={sourceColumns ?? []}
          onFieldChange={handleFieldChange}
          onSubmit={handleSubmit}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose()}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          loading={isSubmitting}
          disabled={isSubmitting}
        >
          Update mapping
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MappingEditDialog;
